import { createContext, useContext } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { VisitWindow } from '../../../utils/observedAt';
import type { EntryVisibility } from '../../../dtos/patientmr/charting';
import type { Entry, OutputFormData, RespiratoryFormData, SeizureFormData, SupervisoryFormData, TherapyFormData, AideTasksFormData } from './types';
import type { EntryErrors } from './model/entryValidation';
import type { TemplateNoteFormData } from './model/noteTemplates';

/** The visit the chart belongs to (from GET …/visits/{visitId}) */
export interface ChartVisitInfo {
  visitId: string;
  visitType?: string | null;
  discipline?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}

/**
 * Phase 18 entry-form state that is not passed down as props: the new entry types
 * (respiratory, seizure, output, supervisory note), validation, server field errors, the
 * visit's entries (copy last, rescue med link, PRN follow-ups) and list actions.
 * Provided by charting/index.tsx; every consumer works without it (tests, older callers).
 */
export interface ChartFormState {
  respiratoryFormData: RespiratoryFormData;
  setRespiratoryFormData: Dispatch<SetStateAction<RespiratoryFormData>>;
  seizureFormData: SeizureFormData;
  setSeizureFormData: Dispatch<SetStateAction<SeizureFormData>>;
  outputFormData: OutputFormData;
  setOutputFormData: Dispatch<SetStateAction<OutputFormData>>;
  supervisoryFormData: SupervisoryFormData;
  setSupervisoryFormData: Dispatch<SetStateAction<SupervisoryFormData>>;
  /** Phase 20C therapy treatment note */
  therapyFormData?: TherapyFormData;
  setTherapyFormData?: Dispatch<SetStateAction<TherapyFormData>>;
  /** Phase 20D aide / attendant task note */
  aideTasksFormData?: AideTasksFormData;
  setAideTasksFormData?: Dispatch<SetStateAction<AideTasksFormData>>;
  /** Phase 20E: the open note template's fields */
  templateNoteFormData?: TemplateNoteFormData;
  setTemplateNoteFormData?: Dispatch<SetStateAction<TemplateNoteFormData>>;
  /** Phase 20F: the patient is in the palliative care programme (symptom entry) */
  palliative?: boolean;
  /** Phase 20C: the patient (care-plan goals for the treatment note) */
  patientId?: string;
  /** Client validation of the open form of this local type */
  errorsFor: (type: string | null | undefined) => EntryErrors;
  /** 400 ENTRY_INVALID `fields[]` of the last save, keyed by data path */
  serverFieldErrors: EntryErrors;
  /** Entries of the visit on screen */
  visitEntries: Entry[];
  /** M7: last GIVEN time per medication id in this visit */
  lastGivenByMedication: Record<string, string>;
  visit: ChartVisitInfo | null;
  visitWindow: VisitWindow;
  /** M8 "Copy last": prefill from the latest entry of the type; false when there is none */
  copyLast: (type: string) => boolean;
  hasLast: (type: string) => boolean;
  /** M7 "Record effect" on a PRN GIVEN entry */
  recordEffect?: (entry: Entry) => void;
  /** M6: supervisors change the visibility of another author's entry (reason required) */
  canSuperviseVisibility: boolean;
  changeVisibility?: (entry: Entry, visibility: EntryVisibility, reason: string) => Promise<void>;
  /** The chart is a DRAFT (entries may be added) */
  chartEditable: boolean;
  /** The signed-in user (left out of the F21 supervised staff picker) */
  currentUserId?: string;
  /** Phase 18U U6: the entry type whose open form was prefilled by "Copy last" in the Add sheet */
  copiedFromLast?: string | null;
  /** Phase 18U U9: "Resume unsaved changes?" for the entry being edited */
  editDraftNotice?: ReactNode;
}

export const ChartFormContext = createContext<ChartFormState | null>(null);

export const useChartForm = (): ChartFormState | null => useContext(ChartFormContext);
